import { useTheme } from '@/features/global/hooks';

import { useChainId, useDappChainConfig } from '@/hooks/web3/useChain';
import { useCallback, useState } from 'react';

import { IMarketPair } from '@/features/market/types';

import { fetchFuturesPairKlineChart } from '@/features/futures/api';
import { useCurrentMarketPairFromUrl } from '@/features/market/hooks';
import { defaultResolutionString, THEME_ENUM_TV_VIEW_MAP, TVResolution } from '../constants';
import { DataFeed } from '../datafeed';
import { useTradingViewStore } from '../store';
import { getSymbolInfo, getWidgetOptions, getWidgetOverrides, resolutionToKlineInterval } from '../utils';
import { useTradingViewBarsUpdate } from './useTradingViewBarsUpdate';

const useTradingViewData = () => {
  const { spotKlineInterval, setSpotKlineInterval, datafeed, setDatafeed } = useTradingViewStore();
  const chainId = useChainId();
  const dappChainConfig = useDappChainConfig(chainId);
  const fetchKLine = useCallback(
    async (
      symbolInfo: TradingView.LibrarySymbolInfo & { pool?: IMarketPair },
      resolution: TradingView.ResolutionString,
      periodParams: TradingView.PeriodParams,
      size: number,
    ) => {
      const klineInterval = resolutionToKlineInterval(resolution);
      const poolAddr = symbolInfo.pool?.instrumentAddr;
      const expiry = symbolInfo.pool?.expiry;

      // const timeEnd = periodParams.to;
      if (!chainId || !poolAddr || !klineInterval || !expiry) return [];

      const res = await fetchFuturesPairKlineChart({
        chainId,
        instrumentAddress: poolAddr,
        expiry,
        klineInterval,
        timeEnd: periodParams.to,
        limit: size,
      });

      return res || [];
    },
    [chainId],
  );

  const getBars = useCallback(
    async (
      symbolInfo: TradingView.LibrarySymbolInfo,
      resolution: TradingView.ResolutionString,
      periodParams: TradingView.PeriodParams,
      onResult: TradingView.HistoryCallback,
      // onError: TradingView.ErrorCallback,
    ) => {
      const bars: TradingView.Bar[] = [];
      // const size = periodParams.countBack;

      if (resolution !== spotKlineInterval) {
        setSpotKlineInterval(resolutionToKlineInterval(resolution));
      }

      try {
        const res = await fetchKLine(symbolInfo, resolution, periodParams, 1000);
        if (!res || !res.length) {
          onResult(bars, { noData: true });
          return;
        }
        bars.push(
          ...res.map((item) => ({
            time: item.timestamp * 1000,
            open: Number(item.open),
            high: Number(item.high),
            low: Number(item.low),
            close: Number(item.close),
            volume: dappChainConfig?.futures?.isHideTradingViewVolume ? undefined : Number(item.baseVolume),
          })),
        );
      } catch (error) {
        // Network errors should trigger a graceful fallback and a scheduled retry
        // Note: TradingView expects we either return noData or a valid bars list
        // onResult(bars, { noData: true });
        // Persist the last request to retry after reconnect or with backoff
        const payload = { symbolInfo, resolution, periodParams, onResult };

        try {
          if (typeof window !== 'undefined') {
            // Local fallback: retry on 'online' event or with exponential backoff
            const handler = () => {
              window.removeEventListener('online', handler);
              getBars(payload.symbolInfo, payload.resolution, payload.periodParams, payload.onResult);
            };
            window.addEventListener('online', handler, { once: true });
          }
        } catch (_) {
          // ignore scheduler failures
        }
        return;
      }
      bars.sort((a, b) => a.time - b.time);

      if (periodParams.firstDataRequest) {
        // subscribeKLine();
      }
      onResult(bars);
    },
    [fetchKLine, setSpotKlineInterval, spotKlineInterval, dappChainConfig],
  );

  const initDatafeed = useCallback(
    (currentPool: IMarketPair) => {
      if (!currentPool?.instrumentAddr) return;
      const supported_resolutions = Object.keys(TVResolution) as never;
      const df = new DataFeed({
        SymbolInfo: getSymbolInfo(currentPool.instrumentAddr, currentPool),
        DatafeedConfiguration: {
          supported_resolutions: supported_resolutions,
        },
        getBars: getBars,
      });

      setDatafeed(df);
    },
    [getBars, setDatafeed],
  );

  return {
    datafeed,
    initDatafeed,
  };
};

export const useTradingView = () => {
  const chainId = useChainId();
  const [loadReady, setLoadReady] = useState(false);
  const { setTradingViewWidget, tradingViewWidget } = useTradingViewStore();

  const currentPair = useCurrentMarketPairFromUrl(chainId);

  const { dataTheme } = useTheme();
  const { datafeed, initDatafeed } = useTradingViewData();
  useTradingViewBarsUpdate(currentPair);
  const initTradingView = useCallback(() => {
    if (tradingViewWidget) {
      // tradingViewWidget.remove();
      return;
    }
    // setTradingViewWidget(null);

    {
      if (!chainId) return;
      if (!currentPair?.instrumentAddr) return;
      if (tradingViewWidget) return;
      const widgetOptions: Omit<TradingView.ChartingLibraryWidgetOptions, 'datafeed'> = getWidgetOptions(
        chainId,
        currentPair?.instrumentAddr,
      );

      if (!datafeed) {
        currentPair && initDatafeed(currentPair);
        return;
      }
      const widgetOverrides = getWidgetOverrides({ theme: THEME_ENUM_TV_VIEW_MAP[dataTheme] });

      if (!widgetOverrides || !window.TradingView) return;
      const tvWidget = new window.TradingView.widget({
        ...widgetOptions,
        ...widgetOverrides,
        datafeed: datafeed!,
      });
      tvWidget.onChartReady(() => {
        setLoadReady(true);

        setTradingViewWidget(tvWidget);
        // tvWidget.headerReady().then(() => {
        //   const button = tvWidget.createButton();
        //   if (button) {
        //     button.setAttribute('title', 'Click to show a notification popup');
        //     button.classList.add('apply-common-tooltip');
        //     button.addEventListener('click', () =>
        //       tvWidget.showNoticeDialog({
        //         title: 'Notification',
        //         body: 'TradingView Charting Library API works correctly',
        //         callback: () => {
        //           console.log('Noticed!');
        //         },
        //       }),
        //     );
        //     button.innerHTML = 'Check API';
        //   }
        // });
      });
      return () => {
        // setTradingViewWidget(null);
        // tvWidget.remove();
        localStorage.setItem('tradingview.current_theme.name', THEME_ENUM_TV_VIEW_MAP[dataTheme]);
      };
      // dataTheme only affect the init one
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [
    tradingViewWidget,
    chainId,
    currentPair?.instrumentAddr,
    dataTheme,
    datafeed,
    initDatafeed,
    setTradingViewWidget,
  ]);
  // useEffect(() => {
  //   initTradingView();
  // }, [initTradingView]);

  // useEffect(() => {
  //   if (!tradingViewWidget || !currentPair) {
  //     console.log('Missing required data:', {
  //       hasWidget: !!tradingViewWidget,
  //       hasPair: !!currentPair,
  //     });
  //     return;
  //   }

  //   console.log('TradingView state:', {
  //     isReady: tradingViewWidget._ready,
  //     hasActiveChart: !!tradingViewWidget?.activeChart?.(),
  //     symbol: currentPair.symbol,
  //   });

  //   // Wait for next tick to ensure chart is fully initialized
  //   setTimeout(() => {
  //     try {
  //       if (tradingViewWidget._ready && tradingViewWidget?.activeChart?.()) {
  //         console.log('Setting symbol to:', currentPair.symbol);
  //         tradingViewWidget.activeChart().setSymbol(currentPair.symbol);
  //       }
  //     } catch (error) {
  //       console.error('Error setting symbol:', error);
  //     }
  //   }, 0);
  // }, [currentPair, tradingViewWidget]);

  return { initTradingView, loadReady };
};

export const useChangeTradingViewSymbol = () => {
  const { tradingViewWidget } = useTradingViewStore();
  const changeSymbol = useCallback(
    (pair: IMarketPair) => {
      if (tradingViewWidget && pair?.instrumentAddr) {
        try {
          tradingViewWidget?.setSymbol(`${pair.instrumentAddr}`, defaultResolutionString, () => {
            //
            void 0;
          });
        } catch (error) {
          console.error('Failed to set symbol:', error);
        }
      }
    },
    [tradingViewWidget],
  );

  return { changeSymbol };
};
